import React, { useState } from 'react';

import { formatDate } from '@betterment/core';
import { CursorProvider } from '@betterment/dataviz';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Separator } from '@betterment/samba-react';

import { ComparisonDropdown } from './benchmark-comparison/ComparisonDropdown';
import { BenchmarkHistoryDocument } from './benchmark-comparison/graphql/benchmarkHistory.generated';
import { BenchmarksDocument } from './benchmark-comparison/graphql/benchmarks.generated';
import { TimeWeightedReturnsHistoryDocument } from './graphql/timeWeightedReturnsHistory.generated';
import { TimeWeightedReturnsChart } from './TimeWeightedReturnsChart';
import { TimeWeightedReturnsChartData } from './TimeWeightedReturnsChartData';

import { trackClick } from '../../../../features/track';
import { DateRangeOption } from '../../../../graphql/schema';
import { CustomDateRange } from '../../../shared/date-range-filter/dateUtils';
import { PerformanceDisclosure } from '../../../user/components/performance-disclosure/PerformanceDisclosure';

export const TimeWeightedReturns: React.FC<{
  dateRangeOption: DateRangeOption;
  accountId: string;
  customDateRange?: CustomDateRange;
}> = ({ dateRangeOption, accountId, customDateRange }) => {
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useState<string | null>(null);

  const { data, loading } = useGraphQLQuery(TimeWeightedReturnsHistoryDocument, {
    variables: {
      dateRangeOption,
      accountIds: [accountId],
      customDateRange,
    },
    throwOnError: true,
  });

  const { data: benchmarksData } = useGraphQLQuery(BenchmarksDocument, {
    throwOnError: true,
  });

  const selectedBenchmarkIds = () => (selectedBenchmarkId ? [selectedBenchmarkId] : []);
  const { data: benchmarkHistoryData } = useGraphQLQuery(BenchmarkHistoryDocument, {
    variables: {
      dateRangeOption,
      benchmarkIds: selectedBenchmarkIds(),
      accountIds: [accountId],
      customDateRange,
    },
    throwOnError: true,
  });

  const timeSeries = data?.performance.timeWeightedReturns.timeSeries;
  const account = data?.performance.accounts[0];
  const portfolioDescription =
    account?.__typename === 'ManagedAccount' ? account.portfolio.configurationDescription : undefined;

  const benchmarks = benchmarksData?.performance.benchmarks ?? [];
  const benchmarkTimeSeries = benchmarkHistoryData?.performance.benchmarks[0]?.timeWeightedReturns?.timeSeries;
  const benchmarkName = benchmarkHistoryData?.performance.benchmarks[0]?.name;

  const endDate = timeSeries && timeSeries.length > 0 ? timeSeries[timeSeries.length - 1].date : undefined;

  const handleTwrDisclosureClick = () => {
    trackClick({
      name: 'twrDisclosure',
      module: 'Performance',
      location: 'TimeWeightedReturns',
    });
  };

  return (
    <Card css={{ overflow: 'visible' }}>
      <Card.Header css={{ padding: 'space.300', display: 'flex', justifyContent: 'space-between' }}>
        <H2 variant="title200">Time-weighted returns</H2>
        <ComparisonDropdown
          benchmarks={benchmarks}
          selectedBenchmarkId={selectedBenchmarkId}
          onBenchmarkChange={setSelectedBenchmarkId}
        />
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300' }}>
        <CursorProvider>
          <TimeWeightedReturnsChartData
            timeSeries={timeSeries}
            benchmarkTimeSeries={benchmarkTimeSeries}
            benchmarkName={benchmarkName}
            loading={loading}
            portfolioDescription={portfolioDescription}
          />
          <TimeWeightedReturnsChart timeSeries={timeSeries} benchmarkTimeSeries={benchmarkTimeSeries} />
        </CursorProvider>
        {endDate && (
          <Box css={{ mt: 'space.200' }}>
            <PerformanceDisclosure
              asOf={`As of ${formatDate(endDate, 'longOrdinalSlashed')}.`}
              performanceDisclosure={data?.disclosures.performanceDisclosure || ''}
              onOpenChange={handleTwrDisclosureClick}
            />
          </Box>
        )}
      </Card.Content>
    </Card>
  );
};
